/**
 * Pure helpers for the Missed Orders page (kanban fc6bca28): labels for a
 * correction's field / status / values and the CSV of the rows on screen
 * (the export columns plus one human_corrections column), kept out of the
 * page module so Fast Refresh sees only components there.
 */
import type { FindingWithCorrections, MissedOrderCorrection } from '../../services/portalMissedOrders';

export const FIELD_LABEL: Record<string, string> = {
  cause_category: 'cause',
  merlin_subcategory: 'subcategory',
  responsible_party: 'responsible party',
  is_miss: 'miss / not a miss',
  pj_category: "PJ's category",
};

/** Where the lesson stands in Merlin's playbook bank (the Knowledge Bank loop). */
export const KB_STATUS_LABEL: Record<string, string> = {
  pending: 'not in Merlin\'s playbook yet',
  queued: 'in Merlin\'s playbook',
  updated: 'in Merlin\'s playbook (updated)',
  deleted: 'removed from Merlin\'s playbook',
  failed: 'playbook write failed',
};

export const STATUS_LABEL: Record<string, string> = {
  recorded: 'recorded',
  challenge_open: 'awaiting PJ',
  challenge_upheld: 'PJ upheld',
  challenge_declined: 'PJ declined',
  withdrawn: 'withdrawn',
};

export function valueLabel(c: MissedOrderCorrection, which: 'merlin' | 'human'): string {
  const v = which === 'merlin' ? c.merlin_value : c.human_value;
  if (v == null || v === '') return '(blank)';
  if (c.field === 'is_miss') return v === 'true' ? 'a miss' : 'not a miss';
  return v;
}

/** The current rows as a CSV (the export columns plus the live corrections), for Jake and Kevin's spreadsheet. */
export function rowsToCsv(rows: FindingWithCorrections[]): string {
  if (rows.length === 0) return '';
  const findingCols = Object.keys(rows[0].finding);
  const header = [...findingCols, 'human_corrections'];
  const cell = (v: unknown): string => {
    if (v == null) return '';
    const s = typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(',')];
  for (const r of rows) {
    const corr = r.corrections
      .filter((c) => c.status !== 'withdrawn')
      .map((c) => `${FIELD_LABEL[c.field] ?? c.field}: ${valueLabel(c, 'human')} (${c.corrected_by_name ?? c.corrected_by_email ?? '?'}, ${STATUS_LABEL[c.status] ?? c.status}): ${c.reason}`)
      .join(' | ');
    const data = r.finding as unknown as Record<string, unknown>;
    lines.push([...findingCols.map((k) => cell(data[k])), cell(corr)].join(','));
  }
  return lines.join('\r\n') + '\r\n';
}
